import MiniAppContainer from "./MiniAppContainer";
import styles from "../../styles/Components/mini-app/MiniDashboard.module.css";
import { MdMoreVert } from "react-icons/md";
import MinimalCard from "./MinimalCard";
import { FaGift, FaRobot, FaSignal } from "react-icons/fa";
import { IoWalletOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { getMinimalDashboardData } from "../../redux/minimal-app/actions";
import { formatAmount, formatPrice } from "../../utility/helper";
import BotBanner from "./mini-dashboard/BotBanner";
import SectionDivider from "./layout/SectionDivider";
import Avatar from "react-avatar";
import { getReferralRewards } from "../../redux/pricing-plan/action";
import AiSignalBanner from "./mini-dashboard/AiSignalBanner";
import ReferralBanner from "./mini-dashboard/ReferralBanner";
import WithdrawButton from "../pricing-plan/referrals/WithdrawButton";
import useSubscriptionPlans from "../../hooks/useSubscriptionPlans";
import useUserData from "../../hooks/useUserData";
import capitalize from "antd/es/_util/capitalize";
import { Skeleton } from "antd";
import { FaArrowRightLong } from "react-icons/fa6";

const returnServiceActivationStatus = (loading, status) => {
  const isActive = status === "True";

  return loading || !status ? (
    "Checking..."
  ) : isActive ? (
    <>
      {/*<FaCircleCheck /> */}
      Active
    </>
  ) : (
    <>
      {/*<IoMdCloseCircle /> */}
      Not Active
    </>
  );
};

function MiniDashboard() {
  const {
    main: { userData },
    minimalAppState: { minimalDashboardStatus, minimalDashboardData },
    profile: { profileData, loading },
    pricingPlan: { referralData, referralRewardsLoading, referralRewardsError },
  } = useSelector((state) => state);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { username, email } = useUserData();

  const {
    planTitle,
    isServiceBased,
    iconColor,
    hasSubscription,
    subscriptionData,
    isLoading,
  } = useSubscriptionPlans();

  const goToCreditChargePage = () => {
    navigate("/mini-pricing-plan");
  };

  useEffect(() => {
    if (
      email &&
      minimalDashboardStatus !== "loading" &&
      minimalDashboardStatus !== "error" &&
      Object.keys(minimalDashboardData).length === 0
    ) {
      dispatch(getMinimalDashboardData(username, email));
    }
  }, [userData, minimalDashboardStatus, minimalDashboardData, dispatch]);

  useEffect(() => {
    if (
      email &&
      !referralData?.user &&
      !referralRewardsLoading &&
      !referralRewardsError
    ) {
      dispatch(getReferralRewards(email, username));
    }
  }, [
    userData,
    referralData,
    dispatch,
    referralRewardsLoading,
    referralRewardsError,
  ]);

  return (
    <MiniAppContainer>
      <div className={styles.title}>
        <h3>Home </h3>
        {/*<MdMoreVert size={20} />*/}
      </div>
      <MinimalCard customClassName={styles.profileBox}>
        <div className={styles.header}>
          <div className={styles.username}>
            <div className={styles.profileImage}>
              {profileData?.avatar_url ? (
                <img
                  className={styles.profileImg}
                  src={profileData?.avatar_url}
                  alt="profile image"
                />
              ) : (
                <Avatar name={username} size="100%" round={true} />
              )}
            </div>
            <h4 className={styles.welcome}>
              Hi{username ? `, ${capitalize(username)}` : null}
            </h4>
          </div>
          <div className={styles.pricingLink} onClick={goToCreditChargePage}>
            <IoWalletOutline size={20} />
          </div>
        </div>

        <div className={styles.profileBoxDetail}>
          <p className={styles.credit}>
            <span className={styles.label}>Account Credit</span>
            {minimalDashboardStatus === "loading" && (
              <Skeleton.Node className={styles.loader} active />
            )}
            <span className={styles.value}>
              {minimalDashboardStatus === "ready" &&
                `$${formatPrice(minimalDashboardData?.credit / 100, 2)}`}
            </span>
          </p>

          <p className={styles.credit}>
            <div className={`${styles.label} ${styles.earnedMoney}`}>
              Earned Money
              <WithdrawButton className={styles.withdrawBtn} />
            </div>
            {referralRewardsLoading && (
              <Skeleton.Node className={styles.loader} active />
            )}
            <span className={styles.value}>
              {!referralRewardsLoading &&
                referralData?.total_cashable_credit &&
                `$${formatAmount(referralData?.total_cashable_credit)}`}
            </span>
          </p>

          <p className={styles.credit}>
            <span className={styles.label}>Subscription</span>
            {isLoading && <Skeleton.Node className={styles.loader} active />}
            <span className={styles.value}>
              {!isLoading &&
                `${planTitle} ${!!isServiceBased ? "(Service based)" : ""}`}
            </span>
          </p>
        </div>
      </MinimalCard>

      <div className={styles.links}>
        <MinimalCard
          customClassName={styles.link}
          clickHandler={() => navigate("/mini-ai-signals")}
        >
          <div className={styles.linkIcon}>
            <FaSignal size={20} />
          </div>
          <div className={styles.body}>
            <span>AI Signals</span>
            <span
              className={`${styles.serviceActivationStatus} ${
                loading
                  ? styles.loading
                  : profileData?.myais_active !== "True"
                    ? styles.notActive
                    : styles.active
              }`}
            >
              {returnServiceActivationStatus(
                loading,
                profileData?.myais_active,
              )}
            </span>
          </div>
        </MinimalCard>
        <MinimalCard
          customClassName={styles.link}
          clickHandler={() => navigate("/mini-bots")}
        >
          <div className={styles.linkIcon}>
            <FaRobot size={20} />
          </div>
          <div className={styles.body}>
            <span>AI Bot</span>
            <span
              className={`${styles.serviceActivationStatus} ${
                loading
                  ? styles.loading
                  : profileData?.mybot_active !== "True"
                    ? styles.notActive
                    : styles.active
              }`}
            >
              {returnServiceActivationStatus(
                loading,
                profileData?.mybot_active,
              )}
            </span>
          </div>
        </MinimalCard>

        <MinimalCard
          customClassName={styles.link}
          clickHandler={() => navigate("/mini-pricing-plan/referrals")}
        >
          <div className={styles.linkIcon}>
            <FaGift size={20} />
          </div>
          <div className={styles.body}>
            <span>Referral</span>
            <span>
              <FaArrowRightLong />
            </span>
          </div>
        </MinimalCard>
      </div>
      <SectionDivider />
      <BotBanner />
      <SectionDivider />
      <AiSignalBanner />
      <SectionDivider />
      <ReferralBanner />
    </MiniAppContainer>
  );
}

export default MiniDashboard;
